import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader2, RefreshCw, Send, Zap, Database } from 'lucide-react';

const SettingsErp = ({ ctx }) => {
  const {
    settings, handleInputChange,
    erpStatus, erpPinging, erpPingResult, handleErpPing,
    erpBackfilling, erpBackfillResult, handleErpBackfill,
  } = ctx;

  const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/erp/webhook`;

  return (
    <>
      <Card className="card-shadow" data-testid="settings-erp-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            ERP-Anbindung (Buchhaltung)
          </CardTitle>
          <CardDescription>
            Alle Rechnungen werden automatisch an ein externes Buchhaltungs-ERP übertragen (Upsert per <code>external_id</code> = Booking-ID).
            Bei Zahlungseingang meldet das ERP per Webhook zurück und die Buchung wird automatisch auf <em>bezahlt</em> gesetzt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="erp_enabled"
              name="erp_enabled"
              checked={!!settings.erp_enabled}
              onChange={handleInputChange}
              className="h-4 w-4"
              data-testid="settings-erp-enabled"
            />
            <Label htmlFor="erp_enabled" className="cursor-pointer">ERP-Sync aktiv</Label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="erp_base_url">ERP Base-URL (ohne Pfad)</Label>
              <Input id="erp_base_url" name="erp_base_url"
                value={settings.erp_base_url || ''} onChange={handleInputChange}
                placeholder="https://erp.example.com"
                data-testid="settings-erp-base-url" />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Vom ERP-Betreiber mitgeteilt.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="erp_mandant">Mandant (Pfad-Segment)</Label>
              <Input id="erp_mandant" name="erp_mandant"
                value={settings.erp_mandant || ''} onChange={handleInputChange}
                placeholder="strongfit"
                data-testid="settings-erp-mandant" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="erp_default_type">Standard-Rechnungstyp</Label>
              <Input id="erp_default_type" name="erp_default_type"
                value={settings.erp_default_type || 'rent'} onChange={handleInputChange}
                placeholder="rent"
                data-testid="settings-erp-default-type" />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                <code>rent</code> → Konto 3410, alles andere → 3400. Pro Buchung überschreibbar.
              </p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="erp_api_key">API-Key (X-API-Key)</Label>
              <Input id="erp_api_key" name="erp_api_key" type="password"
                value={settings.erp_api_key || ''} onChange={handleInputChange}
                placeholder="Leer lassen um bestehenden Key zu behalten"
                className="font-mono text-sm"
                data-testid="settings-erp-api-key" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="erp_webhook_secret">Webhook-Secret (X-Webhook-Secret)</Label>
              <Input id="erp_webhook_secret" name="erp_webhook_secret" type="password"
                value={settings.erp_webhook_secret || ''} onChange={handleInputChange}
                placeholder="Leer lassen um bestehendes Secret zu behalten"
                className="font-mono text-sm"
                data-testid="settings-erp-webhook-secret" />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Muss identisch beim ERP-Betreiber hinterlegt sein. Eingehende Webhooks werden ohne gültiges Secret abgewiesen.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-[hsl(var(--border))] p-3 bg-[hsl(var(--muted))]/30">
            <div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Webhook-URL zum Hinterlegen beim ERP-Betreiber:</div>
            <code className="text-xs break-all block" data-testid="settings-erp-webhook-url">{webhookUrl}</code>
          </div>

          <div className="border-t pt-4 flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline"
              onClick={handleErpPing}
              disabled={erpPinging || !settings.erp_enabled}
              data-testid="settings-erp-ping-btn">
              {erpPinging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Verbindung testen
            </Button>
            <Button type="button" variant="outline"
              onClick={() => handleErpBackfill(false)}
              disabled={erpBackfilling || !settings.erp_enabled}
              data-testid="settings-erp-backfill-btn">
              {erpBackfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Backfill: alle Rechnungen ans ERP senden
            </Button>
            <Button type="button" variant="outline"
              onClick={() => handleErpBackfill(true)}
              disabled={erpBackfilling || !settings.erp_enabled}
              data-testid="settings-erp-backfill-paid-btn">
              {erpBackfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Nur bezahlte
            </Button>
          </div>

          {erpPingResult && (
            <Alert variant={erpPingResult.type === 'success' ? 'default' : 'destructive'}>
              <AlertDescription className="text-xs">{erpPingResult.message}</AlertDescription>
            </Alert>
          )}
          {erpBackfillResult && (
            <Alert variant={erpBackfillResult.type === 'success' ? 'default' : 'destructive'}>
              <AlertDescription className="text-xs">{erpBackfillResult.message}</AlertDescription>
            </Alert>
          )}

          {erpStatus && (
            <div className="grid grid-cols-3 gap-3 text-center pt-2">
              <div className="rounded-md border border-[hsl(var(--border))] p-3">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Ausstehend</div>
                <div className="text-lg font-semibold" data-testid="erp-queue-pending">{erpStatus.queue?.pending ?? 0}</div>
              </div>
              <div className="rounded-md border border-[hsl(var(--border))] p-3">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Erledigt</div>
                <div className="text-lg font-semibold text-emerald-600" data-testid="erp-queue-done">{erpStatus.queue?.done ?? 0}</div>
              </div>
              <div className="rounded-md border border-[hsl(var(--border))] p-3">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Fehlgeschlagen</div>
                <div className="text-lg font-semibold text-red-600" data-testid="erp-queue-failed">{erpStatus.queue?.failed ?? 0}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default SettingsErp;
